/**
 * De quoi il s'agit, et ce qu'on y fait : une prestation collée ou dictée,
 * rangée comme le patron l'écrit.
 *
 * <p>« Il faut dire de quoi il s'agit, et après noter ce qu'on fait. » La
 * désignation nomme l'ouvrage et la pièce (« Plafond, salle de bain ») ; la
 * description dit le travail (« Préparation du plafond, masticage, ponçage,
 * prêt à peindre »). Un texte collé commence souvent par le travail
 * (« Préparation du plafond de la salle de bain ») : l'ouvrage et la pièce en
 * sont tirés pour la désignation, et la phrase passe en description.
 *
 * <p>Formes lues (bâtiment, en français) :
 * <ul>
 *   <li>« Préparation du plafond de la salle de bain » → « Plafond, salle de
 *       bain » / « Préparation du plafond de la salle de bain » ;</li>
 *   <li>« Peinture des murs et du plafond de la chambre 2 » → « Murs et
 *       plafond, chambre 2 » ;</li>
 *   <li>« Plafond salle de bain : préparation, masticage » → « Plafond salle
 *       de bain » / « Préparation, masticage » ;</li>
 *   <li>« Plafond, salle de bain » suivi de sa description : inchangé.</li>
 * </ul>
 * Un travail qui ne nomme ni ouvrage ni pièce (« Déplacement et mise en place
 * du chantier ») garde son intitulé.
 */

/** Les mots du travail : ce qu'on fait. */
const MOTS_TRAVAIL = [
  'fourniture et pose',
  'fourniture',
  'f\\+p',
  'pose',
  'dépose',
  'repose',
  'préparation',
  'mise en peinture',
  'mise en place',
  'mise à niveau',
  'peinture',
  'repeindre',
  'ponçage',
  'masticage',
  'enduisage',
  'enduit',
  'ragréage',
  'réparation',
  'remplacement',
  'installation',
  'démontage',
  'montage',
  'démolition',
  'nettoyage',
  'lessivage',
  'décapage',
  'sablage',
  'traitement',
  'isolation',
  'rénovation',
  'réfection',
  'rafraîchissement',
  'protection',
  'évacuation',
  'arrachage',
  'raccordement',
  'création',
  'ouverture',
  'fermeture',
  'rebouchage',
  'imprégnation',
  'vitrification',
  'jointoiement',
  'crépissage',
  'application',
  'habillage',
  'doublage',
  'égrenage',
  'vernissage',
  'lasurage',
  'ravalement',
  'étanchéité',
  'remise en état',
  'rénover',
  'réparer',
  'remplacer',
  'poser',
  'peindre',
  'poncer',
  'nettoyer',
  'préparer',
  'déplacement',
  'découpe',
];

const UN_TRAVAIL = '(?:' + MOTS_TRAVAIL.join('|') + ')s?';

/** Le travail, en tête de l'intitulé. */
const TRAVAIL = new RegExp('^' + UN_TRAVAIL + '(?![\\p{L}])', 'iu');

/** Les travaux qui sont eux-mêmes le titre, quoi qu'ils touchent. */
const TITRE_DU_TRAVAIL =
  /^(?:déplacements?|protections?|démolitions?|nettoyages?|évacuations?|installations? de chantier)(?![\p{L}])/iu;

/** Ce qui vient en plus du sujet : « y compris… », « avec… », « sauf… ». */
const APRES_LE_SUJET = /\s+(?:y compris|avec|sauf|ainsi que)(?![\p{L}]).*$/isu;

/** Plusieurs travaux liés en tête : « Fournitures et application », « Dépose, repose ». */
const TRAVAUX_LIES = new RegExp(
  '^' + UN_TRAVAIL + '(?:\\s*(?:,|\\bet\\b|\\+)\\s*' + UN_TRAVAIL + ')+(?![\\p{L}])',
  'iu',
);

/** Ce qui reçoit le travail : « … d'une couche d'accrochage », « … du chantier ». */
const COMPLEMENT = /(?:^|\s)(?:d[’']une?\s+|du\s+|de\s+la\s+|de\s+l[’']|des\s+|d[’'])(\p{L}.*)$/iu;

/** Ce qui suit le complément sans le nommer : « y compris… », « avec… ». */
const FIN_DU_COMPLEMENT =
  /\s*(?:,|\by compris\b|\bavec\b|\bpour\b|\bsur\b|\bdans\b|\bselon\b|\bsous\b|\bau\b|\baux\b|\bentre\b|\bcontre\b).*$/iu;

/** Les revêtements : posés sur le sol ou le mur, c'est eux le sujet. */
const REVETEMENT =
  /^(?:carrelages?|faïences?|parquets?|moquettes?|revêtements?|crépis?|papiers? peints?|chapes?|joints?)/iu;

/** Un mot du travail n'importe où dans l'intitulé. */
const TRAVAIL_DANS = new RegExp('(?<![\\p{L}])' + UN_TRAVAIL + '(?![\\p{L}])', 'giu');

/** Les pièces, la plus longue d'abord ; « chambre 2 » garde son numéro. */
const PIECES = [
  'salles? de bains?',
  "salles? d[’']eau",
  'salles? de douche',
  'salle à manger',
  "cages? d[’']escaliers?",
  'cuisines?',
  'chambres?',
  'salons?',
  'séjours?',
  'couloirs?',
  'corridors?',
  'halls?',
  'entrées?',
  'vestibules?',
  'w\\.?c\\.?',
  'toilettes',
  'caves?',
  'garages?',
  'buanderies?',
  'bureaux',
  'bureau',
  'greniers?',
  'combles',
  'balcons?',
  'terrasses?',
  'vérandas?',
  'dressings?',
  'mezzanines?',
  'appartements?',
  'studios?',
  'local',
  'locaux',
];
const PIECE = new RegExp(
  '(?<![\\p{L}])(' + PIECES.join('|') + ')(?:\\s+(?:n°\\s*)?\\d{1,3}\\b)?(?![\\p{L}])',
  'iu',
);

/** Les ouvrages : ce qui reçoit le travail. */
const OUVRAGES = [
  'papiers? peints?',
  'faux[- ]plafonds?',
  'garde-corps',
  'plafonds?',
  'murs?',
  'parois?',
  'sols?',
  'planchers?',
  'parquets?',
  'carrelages?',
  'faïences?',
  'portes?',
  'fenêtres?',
  'volets?',
  'stores?',
  'radiateurs?',
  'plinthes?',
  'façades?',
  'toitures?',
  'escaliers?',
  'cloisons?',
  'boiseries?',
  'balustrades?',
  'chapes?',
  'dalles?',
  'menuiseries?',
  'encadrements?',
  'embrasures?',
  'tablettes?',
  'corniches?',
  'moulures?',
  'sanitaires?',
  'baignoires?',
  'douches?',
  'lavabos?',
  'éviers?',
  'joints?',
  'vitrages?',
  'gouttières?',
  'chéneaux',
  'poutres?',
  'charpentes?',
  'crépis?',
  'revêtements?',
  'moquettes?',
];
// « carrelage mural », « peinture des murs extérieurs » : l'adjectif reste à l'ouvrage.
const OUVRAGE = new RegExp(
  '(?<![\\p{L}])((?:' +
    OUVRAGES.join('|') +
    ')(?:\\s+(?:mural|murale|muraux|murales|extérieure?s?|intérieure?s?|extérieurs|intérieurs))?)(?![\\p{L}])',
  'giu',
);

/** « Plafond salle de bain : préparation… », « … – préparation… ». */
const SEPARATEUR = /^([^:\d]{2,60}?)\s*(?::|\s[-–—]\s)\s*(\p{L}.*)$/su;

const majuscule = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const minuscule = (t) => (/^\p{Lu}\p{Ll}/u.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t);

/** La description : le travail écrit en tête, puis ce qui était déjà dessous. */
function description(travail, suite) {
  const s = String(suite || '').trim();
  if (!s) return travail;
  // Une seule ligne : elle prolonge la phrase (« Préparation du plafond,
  // ponçage, prêt à peindre »). Plusieurs : elles gardent leurs lignes.
  if (!s.includes('\n')) return travail.replace(/[\s.,;:]+$/, '') + ', ' + minuscule(s);
  return travail + '\n' + s;
}

/**
 * L'ouvrage et la pièce nommés par un intitulé qui commence par le travail.
 *
 * @param {string} intitule « Préparation du plafond de la salle de bain »
 * @returns {string|null} « Plafond, salle de bain », ou null
 */
export function objetDuTravail(intitule) {
  const t = String(intitule || '').trim();
  const travail = t.match(TRAVAIL);
  if (!travail) return null;
  // « Déplacement… », « Protection… », « Démolition… » : le travail est le titre.
  if (TITRE_DU_TRAVAIL.test(t)) return majuscule(travail[0].toLowerCase());
  // « … y compris coller avec les joints » : les joints ne sont pas le sujet.
  const sujet = t.replace(APRES_LE_SUJET, '');
  const piece = sujet.match(PIECE);
  // La pièce lue n'est plus un ouvrage : « cage d'escalier » n'est pas un
  // escalier, « douche » dans « salle de douche » non plus.
  const reste = piece ? sujet.replace(piece[0], ' ') : sujet;
  const ouvrages = [];
  for (const m of reste.matchAll(OUVRAGE)) {
    const o = m[1].toLowerCase();
    if (!ouvrages.includes(o)) ouvrages.push(o);
  }
  if (!ouvrages.length && !piece) return null;
  // « Pose du carrelage sol et au mur » : le carrelage est le sujet, le sol
  // et le mur disent seulement où il va.
  if (ouvrages.length > 1 && REVETEMENT.test(ouvrages[0])) ouvrages.splice(1);
  // « Sanitaire, baignoire et lavabo » : pas « Sanitaire et baignoire et lavabo ».
  const quoi =
    ouvrages.length > 2
      ? ouvrages.slice(0, -1).join(', ') + ' et ' + ouvrages[ouvrages.length - 1]
      : ouvrages.join(' et ');
  const ou = piece ? piece[0].trim().replace(/^w\.?c\.?$/i, 'WC') : '';
  return majuscule([quoi, quoi && ou !== 'WC' ? ou.toLowerCase() : ou].filter(Boolean).join(', '));
}

/**
 * Coupe un intitulé dicté qui enchaîne deux travaux : le premier reste en
 * désignation, la suite passe en description. « Déplacement mise en place
 * du chantier » → « Déplacement » / « Mise en place du chantier ».
 *
 * @param {string} intitule l'intitulé dicté, sans virgule
 * @returns {{d: string, sd: string}|null} l'intitulé coupé, ou null
 */
export function separeLesTravaux(intitule) {
  const t = String(intitule || '').trim();
  for (const m of t.matchAll(TRAVAIL_DANS)) {
    if (m.index === 0) continue;
    const avant = t
      .slice(0, m.index)
      .replace(/[\s,;:+]+$/, '')
      .replace(/\s+(?:et|puis|avec)$/i, '')
      .trim();
    if (!avant) continue;
    return { d: majuscule(avant), sd: majuscule(t.slice(m.index).trim()) };
  }
  return null;
}

/**
 * Le complément d'un intitulé qui commence par le travail : « Fournitures et
 * application d'une couche d'accrochage » → « Couche d'accrochage ».
 *
 * @param {string} intitule l'intitulé, travail en tête
 * @returns {string|null} le complément, ou null
 */
function complementDuTravail(intitule) {
  const t = String(intitule || '').trim();
  const tete = t.match(TRAVAUX_LIES) || t.match(TRAVAIL);
  if (!tete) return null;
  const m = t.slice(tete[0].length).match(COMPLEMENT);
  if (!m) return null;
  const quoi = m[1].replace(FIN_DU_COMPLEMENT, '').trim();
  if (!quoi || quoi.split(/\s+/).length > 5) return null;
  return majuscule(quoi);
}

/**
 * Vrai quand une prestation nomme ce qui reçoit le travail : « … d'une
 * couche d'accrochage », « … du chantier ». « Masticage, ponçage » non.
 *
 * @param {string} texte la prestation
 * @returns {boolean} un complément est nommé
 */
export function aUnComplement(texte) {
  return COMPLEMENT.test(String(texte || '').trim());
}

/** Les mots après lesquels un travail continue la même prestation. */
const LIAISON =
  /(?:^|\s)(?:et|puis|ou|à|au|aux|de|du|des|la|le|les|une?|pour|avec|après|avant|sans|y compris|d[’']|l[’'])$|[,+]$|[’']$/iu;

/**
 * Coupe une dictée vocale sans ponctuation en prestations : le micro du
 * téléphone n'écrit ni virgule ni point. « Déplacement mise en place du
 * chantier protection de la zone de travail fournitures et application d'une
 * couche d'accrochage » → trois prestations. Une nouvelle prestation commence
 * à un mot du travail, quand ce qui précède en est une complète (trois mots au
 * moins, sans « et », « de »… en dernier).
 *
 * @param {string} texte la dictée
 * @returns {string[]} les prestations
 */
export function coupeLaDictee(texte) {
  const t = String(texte || '').trim();
  const morceaux = [];
  let debut = 0;
  for (const m of t.matchAll(TRAVAIL_DANS)) {
    const avant = t.slice(debut, m.index).trim();
    if (!avant || LIAISON.test(avant) || avant.split(/\s+/).length < 3) continue;
    morceaux.push(avant);
    debut = m.index;
  }
  morceaux.push(t.slice(debut).trim());
  return morceaux.filter(Boolean);
}

/**
 * Range une prestation collée : de quoi il s'agit en désignation, ce qu'on
 * fait en description.
 *
 * @param {{d: string, sd?: string}} ligne la prestation lue
 * @returns {{d: string, sd: string}} la désignation et la description rangées
 */
export function placeObjetEtTravail({ d, sd }) {
  const titre = String(d || '').trim();
  const suite = String(sd || '').trim();
  // « Plafond salle de bain : préparation, masticage » : déjà dans l'ordre.
  const coupe = titre.match(SEPARATEUR);
  if (coupe && !TRAVAIL.test(coupe[1].trim()) && coupe[1].trim().split(/\s+/).length <= 6) {
    return { d: coupe[1].trim(), sd: description(majuscule(coupe[2].trim()), suite) };
  }
  // « Fournitures et application d'une couche d'accrochage au mur et au
  // sol » : ce qu'on fournit et applique est le sujet, pas le mur.
  if (!suite && TRAVAUX_LIES.test(titre)) {
    const quoi = complementDuTravail(titre);
    if (quoi) return { d: quoi, sd: majuscule(titre) };
  }
  const objet = objetDuTravail(titre);
  if (objet && titre.toLowerCase().startsWith(objet.toLowerCase())) {
    // Le travail en titre : « Déplacement », « Protection », « Démolition ».
    const reste = titre.slice(objet.length).trim();
    // Déjà rangé par l'artisan (un titre, sa description) : on n'y touche pas.
    if (reste && suite) return { d: titre, sd: suite };
    // Un second travail suit (« mise en place du chantier ») : il passe
    // seul dessous ; sinon (« final du chantier ») la phrase reste entière.
    const phrase = TRAVAIL.test(reste) ? majuscule(reste) : titre;
    return { d: objet, sd: reste ? description(phrase, suite) : suite };
  }
  if (objet) return { d: objet, sd: description(titre, suite) };
  // Sans description, la phrase entière ne reste pas en titre : « Déplacement
  // mise en place du chantier » → « Déplacement » / « Mise en place du
  // chantier » ; « Fournitures et application d'une couche d'accrochage » →
  // « Couche d'accrochage », la phrase en description.
  if (!suite) {
    const lies = TRAVAUX_LIES.test(titre);
    const coupe = lies ? null : separeLesTravaux(titre);
    if (coupe) return coupe;
    const quoi = complementDuTravail(titre);
    if (quoi) return { d: quoi, sd: majuscule(titre) };
  }
  return { d: titre, sd: suite };
}
